import type { TableType } from '../../types';

interface TableSchemeProps {
  type: TableType;
  seats: number;
  filled: number;
}

const DOT_R = 2.6;
const MAX_DOTS = 12;

function rectSeats(x1: number, y1: number, x2: number, y2: number, n: number): { x: number; y: number }[] {
  const w = x2 - x1;
  const h = y2 - y1;
  const per = 2 * (w + h);
  const pts: { x: number; y: number }[] = [];
  for (let i = 0; i < n; i++) {
    const d = ((i + 0.5) / n) * per;
    if (d < w) pts.push({ x: x1 + d, y: y1 });
    else if (d < w + h) pts.push({ x: x2, y: y1 + (d - w) });
    else if (d < 2 * w + h) pts.push({ x: x2 - (d - w - h), y: y2 });
    else pts.push({ x: x1, y: y2 - (d - 2 * w - h) });
  }
  return pts;
}

function seatPositions(type: TableType, n: number): { x: number; y: number }[] {
  if (type === 'round') {
    const pts: { x: number; y: number }[] = [];
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 - Math.PI / 2;
      pts.push({ x: 26 + Math.cos(a) * 18, y: 26 + Math.sin(a) * 18 });
    }
    return pts;
  }
  if (type === 'head') {
    const pts: { x: number; y: number }[] = [];
    for (let i = 0; i < n; i++) {
      pts.push({ x: 13 + ((i + 0.5) / n) * 26, y: 14 });
    }
    return pts;
  }
  if (type === 'square') return rectSeats(8, 8, 44, 44, n);
  return rectSeats(5, 12, 47, 40, n);
}

function CenterShape({ type }: { type: TableType }) {
  const stroke = { stroke: 'var(--st-accent)', fill: 'none' } as const;
  if (type === 'round') return <circle cx="26" cy="26" r="11" strokeWidth="1.6" style={stroke} />;
  if (type === 'square') return <rect x="16" y="16" width="20" height="20" rx="3" strokeWidth="1.6" style={stroke} />;
  if (type === 'rectangular') return <rect x="12" y="20" width="28" height="12" rx="3" strokeWidth="1.6" style={stroke} />;
  return <rect x="12" y="23" width="28" height="8" rx="2" strokeWidth="1.6" style={stroke} />;
}

export default function TableScheme({ type, seats, filled }: TableSchemeProps) {
  const n = Math.min(Math.max(seats, 1), MAX_DOTS);
  const dots = seatPositions(type, n);
  const filledDots = Math.min(Math.max(filled, 0), n);

  return (
    <svg
      className="st-card__scheme"
      viewBox="0 0 52 52"
      aria-hidden="true"
      style={{ background: 'color-mix(in srgb, var(--st-accent) 8%, var(--st-card))', borderRadius: '50%' }}
    >
      <CenterShape type={type} />
      {dots.map((p, i) => (
        <circle
          key={i}
          cx={p.x}
          cy={p.y}
          r={DOT_R}
          strokeWidth="1.4"
          style={i < filledDots
            ? { fill: 'var(--st-accent)', stroke: 'var(--st-accent)' }
            : { fill: 'none', stroke: 'var(--st-accent)' }}
        />
      ))}
    </svg>
  );
}
